import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCartIcon, HeartIcon, ShareIcon, CheckIcon, StarIcon, TagIcon, CalendarIcon, UserIcon, EyeIcon, DownloadIcon, PlayIcon, LockIcon } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { VideoPlayer } from '../components/video/VideoPlayer';
import { ResponsiveVideoPlayer } from '../components/video/ResponsiveVideoPlayer';
import { useProduct } from '../hooks/useFirestore';
import { useAuth } from '../components/auth/AuthProvider';
import { useCart } from '../components/cart/CartContext';
import { useFavorites } from '../hooks/useFavorites';
import { motion } from 'framer-motion';
// Move helper function outside of component
const getVideoPlayerStyles = (isVertical = false) => {
  if (isVertical) {
    return {
      maxHeight: '30vh',
      maxWidth: '100%',
      margin: '0 auto'
    };
  }
  return {
    width: '100%',
    height: 'auto',
    maxHeight: '30vh'
  };
};
export const ProductDetailPage = () => {
  const {
    productId
  } = useParams<{
    productId: string;
  }>();
  const {
    product,
    loading,
    error
  } = useProduct(productId);
  const {
    isAuthenticated,
    user
  } = useAuth();
  const {
    addToCart
  } = useCart();
  const navigate = useNavigate();
  const {
    isFavorited,
    toggleFavorite
  } = useFavorites();
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [purchaseStatus, setPurchaseStatus] = useState<'idle' | 'adding' | 'added' | 'error'>('idle');
  const [showDownloadMessage, setShowDownloadMessage] = useState(false);
  // Set the selected image once the product is loaded
  useEffect(() => {
    if (product && product.image) {
      setSelectedImage(product.image);
    }
  }, [product]);
  // Handle add to cart action
  const handleAddToCart = () => {
    if (!isAuthenticated) {
      navigate('/sell-with-us', {
        state: {
          openAuth: true,
          redirectTo: `/product/${productId}`
        }
      });
      return;
    }
    if (!product) return;
    setPurchaseStatus('adding');
    try {
      addToCart({
        id: product.id || '',
        title: product.title,
        price: product.price,
        image: product.image || '',
        quantity: 1,
        sellerId: product.sellerId || '',
        sellerName: product.sellerName || 'Unknown Seller'
      });
      setPurchaseStatus('added');
      // Reset status after 3 seconds
      setTimeout(() => {
        setPurchaseStatus('idle');
      }, 3000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      setPurchaseStatus('error');
      // Reset status after 3 seconds
      setTimeout(() => {
        setPurchaseStatus('idle');
      }, 3000);
    }
  };
  // Buy now disabled — checkout not yet implemented
  const handleBuyNow = () => {
    // Checkout is coming soon — do nothing
  };
  // Toggle wishlist/favorites status
  const toggleWishlist = () => {
    if (productId) {
      toggleFavorite(productId);
    }
  };
  const isInWishlist = productId ? isFavorited(productId) : false;
  // Share the product
  const shareProduct = () => {
    setShowShareOptions(!showShareOptions);
  };
  // Debug: log product data to verify productFileUrl is present
  useEffect(() => {
    if (product) {
      console.log('[ProductDetailPage] product data:', {
        id: product.id,
        title: product.title,
        productFileUrl: product.productFileUrl,
        hasProductFileUrl: !!product.productFileUrl,
        allKeys: Object.keys(product)
      });
    }
  }, [product]);
  // Determine if product is a video
  const isVideoProduct = product?.category === 'Short-form Video';
  const isVerticalVideo = product?.videoMetadata?.isVertical || false;
  // Loading state
  if (loading) {
    return <>
        <Navbar scrolled={true} data-id="element-6321" />
        <div className="container mx-auto px-4 py-24 flex justify-center items-center min-h-screen" data-id="element-6322">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2DB489]" data-id="element-6323"></div>
        </div>
        <Footer data-id="element-6324" />
      </>;
  }
  // Error state
  if (error || !product) {
    return <>
        <Navbar scrolled={true} data-id="element-6325" />
        <div className="container mx-auto px-4 py-24 flex flex-col justify-center items-center min-h-screen" data-id="element-6326">
          <div className="bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 p-4 rounded-lg mb-4" data-id="element-6327">
            <p data-id="element-6328">Error loading product. Please try again later.</p>
            {error && <p className="text-sm mt-2" data-id="element-6329">{error.message}</p>}
          </div>
          <button onClick={() => navigate('/browse')} className="mt-4 bg-[#2DB489] dark:bg-[#4ED1A0] text-white px-6 py-2 rounded-lg hover:bg-opacity-90 transition-all" data-id="element-6330">
            Browse Products
          </button>
        </div>
        <Footer data-id="element-6331" />
      </>;
  }
  // Format the product description for display
  const formatDescription = (description: string) => {
    if (!description) return '';
    // If showing full description or description is short, return it as is
    if (showFullDescription || description.length < 300) {
      return description;
    }
    // Otherwise truncate it
    return description.substring(0, 300) + '...';
  };
  return <>
      <Navbar scrolled={true} data-id="element-6332" />
      <main className="container mx-auto px-4 py-12 md:py-16 max-w-7xl" data-id="element-6333">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-6334">
          {/* Left Column - Product Media */}
          <div className="space-y-6" data-id="element-6335">
            {isVideoProduct /* Video Player for video products */ ? <div className={`rounded-xl overflow-hidden bg-black ${isVerticalVideo ? 'vertical-video-container' : 'aspect-video'}`} data-id="element-6336">
                <ResponsiveVideoPlayer videoUrl={product.videoUrl || ''} title={product.title} posterImage={product.image} allowDownload={false} autoPlay={false} videoMetadata={product.videoMetadata} data-id="element-6337" />
              </div> /* Image Gallery for regular products */ : <>
                <div className="rounded-xl overflow-hidden bg-gray-100 dark:bg-[#162825] aspect-video" data-id="element-6338">
                  <img src={selectedImage} alt={product.title} className="w-full h-full object-contain" data-id="element-6339" />
                </div>
                {/* Thumbnail gallery */}
                {product.previewImages && product.previewImages.length > 0 && <div className="flex space-x-3 overflow-x-auto pb-2" data-id="element-6340">
                    <div className={`w-20 h-20 rounded-lg overflow-hidden cursor-pointer border-2 ${selectedImage === product.image ? 'border-[#2DB489] dark:border-[#4ED1A0]' : 'border-transparent'}`} onClick={() => setSelectedImage(product.image || '')} data-id="element-6341">
                      <img src={product.image} alt={`${product.title} - main`} className="w-full h-full object-cover" data-id="element-6342" />
                    </div>
                    {product.previewImages.map((img, index) => <div key={index} className={`w-20 h-20 rounded-lg overflow-hidden cursor-pointer border-2 ${selectedImage === img ? 'border-[#2DB489] dark:border-[#4ED1A0]' : 'border-transparent'}`} onClick={() => setSelectedImage(img)} data-id="element-6343">
                        <img src={img} alt={`${product.title} - preview ${index + 1}`} className="w-full h-full object-cover" data-id="element-6344" />
                      </div>)}
                  </div>}
              </>}
          </div>

          {/* Right Column - Product Info */}
          <div className="space-y-6" data-id="element-6345">
            {/* Product category */}
            <div data-id="element-6346">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400" data-id="element-6347">
                <TagIcon className="w-3 h-3 mr-1" data-id="element-6348" />
                {product.category}
              </span>
            </div>

            {/* Product title */}
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-[#F8FAF9]" data-id="element-6349">
              {product.title}
            </h1>

            {/* Seller info and date */}
            <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-[#7A8F8A]" data-id="element-6350">
              <div className="flex items-center" data-id="element-6351">
                <UserIcon className="w-4 h-4 mr-1" data-id="element-6352" />
                <span data-id="element-6353">By {product.sellerName || 'Unknown Seller'}</span>
              </div>
              <div className="flex items-center" data-id="element-6354">
                <CalendarIcon className="w-4 h-4 mr-1" data-id="element-6355" />
                <span data-id="element-6356">
                  Added{' '}
                  {new Date(product.createdAt || Date.now()).toLocaleDateString()}
                </span>
              </div>
            </div>

            {/* Price */}
            <div className="flex items-baseline" data-id="element-6357">
              <span className="text-3xl font-bold text-gray-900 dark:text-white" data-id="element-6358">
                ${product.price}
              </span>
              {product.originalPrice && product.originalPrice > product.price && <span className="ml-2 text-lg text-gray-500 dark:text-[#7A8F8A] line-through" data-id="element-6359">
                    ${product.originalPrice}
                  </span>}
            </div>

            {/* Description */}
            <div data-id="element-6360">
              <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-6361">
                Description
              </h2>
              <p className="text-gray-600 dark:text-[#B0C1BD] whitespace-pre-line" data-id="element-6362">
                {formatDescription(product.description)}
              </p>
              {product.description && product.description.length > 300 && <button onClick={() => setShowFullDescription(!showFullDescription)} className="mt-2 text-[#2DB489] dark:text-[#4ED1A0] text-sm font-medium hover:underline" data-id="element-6363">
                  {showFullDescription ? 'Show less' : 'Read more'}
                </button>}
            </div>

            {/* Features */}
            {isVideoProduct ? <div data-id="element-6364">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-6365">
                  Video Details
                </h2>
                <ul className="space-y-2" data-id="element-6366">
                  <li className="flex items-center text-gray-600 dark:text-[#B0C1BD]" data-id="element-6367">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-6368" />
                    <span data-id="element-6369">Video Type: {product.videoType || 'Standard'}</span>
                  </li>
                  <li className="flex items-center text-gray-600 dark:text-[#B0C1BD]" data-id="element-6370">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-6371" />
                    <span data-id="element-6372">Commercial Use License Included</span>
                  </li>
                  <li className="flex items-center text-gray-600 dark:text-[#B0C1BD]" data-id="element-6373">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-6374" />
                    <span data-id="element-6375">Immediate Download After Purchase</span>
                  </li>
                </ul>
              </div> : <div data-id="element-6376">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-[#F8FAF9] mb-2" data-id="element-6377">
                  Features
                </h2>
                <ul className="space-y-2" data-id="element-6378">
                  {product.format && <li className="flex items-center text-gray-600 dark:text-[#B0C1BD]" data-id="element-6379">
                      <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-6380" />
                      <span data-id="element-6381">Format: {product.format}</span>
                    </li>}
                  {product.licenseType && <li className="flex items-center text-gray-600 dark:text-[#B0C1BD]" data-id="element-6382">
                      <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-6383" />
                      <span data-id="element-6384">License: {product.licenseType}</span>
                    </li>}
                  <li className="flex items-center text-gray-600 dark:text-[#B0C1BD]" data-id="element-6385">
                    <CheckIcon className="w-5 h-5 text-green-500 mr-2" data-id="element-6386" />
                    <span data-id="element-6387">Immediate Download</span>
                  </li>
                </ul>
              </div>}

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 pt-4" data-id="element-6388">
              <motion.button onClick={handleAddToCart} className={`flex-1 flex items-center justify-center px-6 py-3 rounded-lg font-medium text-white ${purchaseStatus === 'adding' ? 'bg-gray-400 dark:bg-gray-600' : purchaseStatus === 'added' ? 'bg-green-500 dark:bg-green-600' : 'bg-[#0A3C52] dark:bg-[#0B3C45] hover:bg-opacity-90'}`} disabled={purchaseStatus === 'adding' || purchaseStatus === 'added'} whileHover={purchaseStatus === 'idle' ? {
              scale: 1.02
            } : {}} whileTap={purchaseStatus === 'idle' ? {
              scale: 0.98
            } : {}} data-id="element-6389">
                {purchaseStatus === 'adding' ? <span className="flex items-center" data-id="element-6390">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-6391">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-6392"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-6393"></path>
                    </svg>
                    Adding...
                  </span> : purchaseStatus === 'added' ? <span className="flex items-center" data-id="element-6394">
                    <CheckIcon className="w-4 h-4 mr-2" data-id="element-6395" />
                    Added to Cart
                  </span> : <span className="flex items-center" data-id="element-6396">
                    <ShoppingCartIcon className="w-4 h-4 mr-2" data-id="element-6397" />
                    Add to Cart
                  </span>}
              </motion.button>
              <motion.button onClick={handleBuyNow} disabled className="flex-1 bg-gray-300 dark:bg-[#1A3330] text-gray-500 dark:text-[#7A8F8A] px-6 py-3 rounded-lg font-medium cursor-not-allowed" data-id="element-6398">
                <span className="flex items-center justify-center" data-id="element-6399">
                  <LockIcon className="w-4 h-4 mr-2" data-id="element-6400" />
                  Checkout Coming Soon
                </span>
              </motion.button>
            </div>

            {/* Download Product button — locked until purchase */}
            {(product.productFileUrl || (product as any).fileUrl) && <div data-id="element-6401">
                <button onClick={() => setShowDownloadMessage(prev => !prev)} className="flex items-center justify-center w-full px-6 py-3 bg-gray-300 dark:bg-[#1A3330] text-gray-500 dark:text-[#7A8F8A] rounded-lg font-medium cursor-pointer transition-all" data-id="element-6402">
                  <LockIcon className="w-4 h-4 mr-2" data-id="element-6403" />
                  Download Product
                </button>
                {showDownloadMessage && <p className="mt-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800/30 rounded-lg px-4 py-2.5 text-center" data-id="element-6404">
                    This is a demo. File download is available after purchase.
                  </p>}
              </div>}

            {/* Secondary actions */}
            <div className="flex space-x-4 pt-2" data-id="element-6405">
              <button onClick={toggleWishlist} className={`flex items-center text-sm ${isInWishlist ? 'text-red-500 dark:text-red-400' : 'text-gray-500 dark:text-[#7A8F8A] hover:text-red-500 dark:hover:text-red-400'}`} data-id="element-6406">
                <HeartIcon className={`w-4 h-4 mr-1 ${isInWishlist ? 'fill-current' : ''}`} data-id="element-6407" />
                {isInWishlist ? 'Saved' : 'Save'}
              </button>
              <div className="relative" data-id="element-6408">
                <button onClick={shareProduct} className="flex items-center text-sm text-gray-500 dark:text-[#7A8F8A] hover:text-[#2DB489] dark:hover:text-[#4ED1A0]" data-id="element-6409">
                  <ShareIcon className="w-4 h-4 mr-1" data-id="element-6410" />
                  Share
                </button>
                {showShareOptions && <div className="absolute left-0 mt-2 w-48 bg-white dark:bg-[#162825] rounded-lg shadow-lg py-2 z-20 border border-gray-100 dark:border-[#1A3330]" data-id="element-6411">
                    <button className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-[#B0C1BD] hover:bg-gray-100 dark:hover:bg-[#1A3330]" onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  setShowShareOptions(false);
                }} data-id="element-6412">
                      Copy Link
                    </button>
                    {/* Add more share options as needed */}
                  </div>}
              </div>
              {product.demoUrl && <a href={product.demoUrl} target="_blank" rel="noopener noreferrer" className="flex items-center text-sm text-gray-500 dark:text-[#7A8F8A] hover:text-[#2DB489] dark:hover:text-[#4ED1A0]" data-id="element-6413">
                  <EyeIcon className="w-4 h-4 mr-1" data-id="element-6414" />
                  Live Preview
                </a>}
            </div>
          </div>
        </div>

        {/* Additional sections like reviews, related products, etc. would go here */}
      </main>
      <Footer data-id="element-6415" />
    </>;
};